import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import Icon from "./Icon";

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/** Mockup `mh()`: sticky header with title, optional extra node and a close button. */
function ModalHeader({ title, extra, onClose, locked, testId }) {
  return (
    <div className="modal-h">
      <h3>{title}</h3>
      {extra}
      <button type="button" className="ibtn" onClick={locked ? undefined : onClose} disabled={locked} aria-label="Close" data-testid={testId ? `${testId}.close` : undefined}><Icon name="x" /></button>
    </div>
  );
}

/** `.modal-b` body wrapper. Pass `as="form"` (+ onSubmit) for form modals. */
export function ModalBody({ as: Tag = "div", className = "", ...rest }) {
  return <Tag className={`modal-b ${className}`.trim()} {...rest} />;
}

/** `.modal-f` sticky footer for action buttons. */
export function ModalFooter({ children }) {
  return <div className="modal-f">{children}</div>;
}

/**
 * Scrim + dialog. Closes on Escape and on scrim mousedown (unless `lock`).
 * `size`: "" | "narrow" | "wide". `title` renders the standard header; omit it to
 * render your own content edge to edge (e.g. a lightbox). Focus moves into the
 * dialog (a `data-autofocus` element if any, else the first field or primary button: confirms of
 * irreversible actions mark their Cancel so one stray Enter can't confirm), Tab is trapped inside
 * it, and focus returns to the opener on close.
 * `testId` lands on the dialog; its header close button gets `${testId}.close`.
 * `label` names the dialog (aria-label) when there is no string `title`, e.g. a lightbox.
 */
export default function Modal({ title, label, headerExtra, size = "", lock = false, onClose, testId, children }) {
  const dlg = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const lockRef = useRef(lock);
  lockRef.current = lock;

  // Mount-only: initial focus, Escape/Tab handling (reads `lock` live), restore focus on unmount.
  useEffect(() => {
    const opener = document.activeElement;
    const el = dlg.current;
    const first = el?.querySelector("[data-autofocus]") || el?.querySelector("input,textarea,select,button.pri") || el?.querySelector(FOCUSABLE);
    first?.focus({ preventScroll: true });
    const onKey = (e) => {
      if (e.key === "Escape" && !lockRef.current) { e.stopPropagation(); closeRef.current?.(); return; }
      if (e.key !== "Tab" || !el) return;
      const f = [...el.querySelectorAll(FOCUSABLE)];
      if (!f.length) return;
      const a = f[0];
      const z = f[f.length - 1];
      if (e.shiftKey && document.activeElement === a) { e.preventDefault(); z.focus(); }
      else if (!e.shiftKey && document.activeElement === z) { e.preventDefault(); a.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("keydown", onKey); opener?.focus?.({ preventScroll: true }); };
  }, []);

  return createPortal(
    <div className="scrim" data-testid={testId ? `${testId}.scrim` : undefined} onMouseDown={(e) => { if (e.target === e.currentTarget && !lock) onClose?.(); }}>
      <div className={`modal ${size}`.trim()} role="dialog" aria-modal="true" aria-label={typeof title === "string" ? title : label || undefined} ref={dlg} data-testid={testId}>
        {title != null && <ModalHeader title={title} extra={headerExtra} onClose={onClose} locked={lock} testId={testId} />}
        {children}
      </div>
    </div>,
    document.body
  );
}
